import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { SortingState } from "@tanstack/react-table";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import {
  listAuditEvents,
  type AuditListSort,
} from "@/shared/api/audit";
import { DataTable } from "@/modules/shell/data-table";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";
import { auditEventColumns } from "./audit-columns";

const PAGE_SIZE = 50;
const SORTABLE: AuditListSort[] = ["createdAt", "action", "docType"];

function sortingToApi(sorting: SortingState): {
  sort: AuditListSort;
  sortDir: "asc" | "desc";
} {
  const first = sorting[0];
  if (first && SORTABLE.includes(first.id as AuditListSort)) {
    return {
      sort: first.id as AuditListSort,
      sortDir: first.desc ? "desc" : "asc",
    };
  }
  return { sort: "createdAt", sortDir: "desc" };
}

export function AuditPage() {
  const { companySlug } = useParams();
  const [docType, setDocType] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const { sort, sortDir } = sortingToApi(sorting);

  const auditQuery = useInfiniteQuery({
    queryKey: queryKeys.audit.list(companySlug ?? "", {
      docType,
      limit: PAGE_SIZE,
      sort,
      sortDir,
    }),
    enabled: Boolean(companySlug),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      listAuditEvents(companySlug!, {
        docType: docType || undefined,
        limit: PAGE_SIZE,
        cursor: pageParam,
        sort,
        sortDir,
      }),
    getNextPageParam: (last) =>
      last.hasMore && last.nextCursor ? last.nextCursor : undefined,
  });
  const errorMsg = queryErrorMessage(auditQuery.error, auditQuery.isError);
  useAuthRedirectOnQueryError(errorMsg);

  const columns = useMemo(
    () => (companySlug ? auditEventColumns(companySlug) : []),
    [companySlug],
  );

  if (!companySlug) return null;

  const events = auditQuery.data?.pages.flatMap((p) => p.events) ?? [];
  const total = auditQuery.data?.pages[0]?.total;
  const displayError =
    errorMsg && errorMsg !== "Not signed in" ? errorMsg : null;

  return (
    <PageFrame>
      <PageHeader
        title="Audit"
        description="Company-wide activity — same events as each doc’s History"
      />

      <label className="mt-6 block text-xs text-[var(--color-muted)]">
        Filter by document type
        <Select
          className="mt-1"
          value={docType}
          onChange={(e) => setDocType(e.target.value)}
        >
          <option value="">All</option>
          <option value="sales_order">Sales orders</option>
          <option value="purchase_order">Purchase orders</option>
          <option value="pos_ticket">POS tickets</option>
          <option value="override_request">Overrides</option>
          <option value="change_request">Change requests</option>
          <option value="customer">Customers</option>
          <option value="product">Products</option>
        </Select>
      </label>
      {events.length > 0 && total != null ? (
        <p className="mt-2 text-xs text-[var(--color-muted)]">
          Showing {events.length}
          {total > events.length ? ` of ${total}` : ""}
        </p>
      ) : null}

      {displayError && (
        <Alert variant="destructive" className="mt-4">
          {displayError}
        </Alert>
      )}

      {auditQuery.isPending && (
        <Alert variant="muted" className="mt-8">
          Loading audit…
        </Alert>
      )}

      {!auditQuery.isPending && events.length === 0 && !displayError ? (
        <div className="mt-8">
          <EmptyState
            title="No audit events yet"
            description="Activity across docs will land here as you work."
          />
        </div>
      ) : null}

      {events.length > 0 ? (
        <>
          <DataTable
            listId="audit-events"
            companySlug={companySlug}
            columns={columns}
            data={events}
            getRowId={(r) => r.id}
            sorting={sorting}
            onSortingChange={setSorting}
            defaultVisibility={{ action: false }}
          />
          {auditQuery.hasNextPage ? (
            <div className="mt-4 flex justify-center">
              <Button
                type="button"
                variant="outline"
                disabled={auditQuery.isFetchingNextPage}
                pending={auditQuery.isFetchingNextPage}
                pendingLabel="Loading more…"
                onClick={() => void auditQuery.fetchNextPage()}
              >
                Load more
              </Button>
            </div>
          ) : null}
        </>
      ) : null}

      <p className="mt-8">
        <Button variant="ghost" asChild>
          <Link to={`/c/${companySlug}`} className="no-underline">
            ← Company
          </Link>
        </Button>
      </p>
    </PageFrame>
  );
}
